import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { X } from 'lucide-react';
import axios from 'axios';
import toast from 'react-hot-toast';
import TinyMCEEditor from './TinyMCEEditor';

const QuickQuestionModal = ({ question, clientId, onClose, onSuccess }) => {
  const isEdit = !!question;
  const [responseContent, setResponseContent] = useState('');
  
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    setValue
  } = useForm();

  useEffect(() => {
    if (question) {
      setValue('title', question.title);
      setResponseContent(question.response);
      setValue('order', question.order || 0);
      setValue('isActive', question.isActive !== false);
    }
  }, [question, setValue]);

  const onSubmit = async (data) => {
    try {
      if (!responseContent.trim()) {
        toast.error('Response content is required');
        return;
      }

      const payload = {
        clientId,
        title: data.title,
        response: responseContent,
        order: parseInt(data.order) || 0,
        isActive: data.isActive
      };

      if (isEdit) {
        await axios.put(`/quick-questions/${question._id}`, payload);
        toast.success('Quick question updated successfully');
      } else {
        await axios.post('/quick-questions', payload);
        toast.success('Quick question created successfully');
      }
      
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Operation failed');
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-4xl w-full max-h-screen overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b">
          <h2 className="text-xl font-semibold">
            {isEdit ? 'Edit Quick Question' : 'Add Quick Question'}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-6 w-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Question Title <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              placeholder="e.g., Kainos, Darbo laikas, Kontaktai"
              {...register('title', { 
                required: 'Title is required',
                maxLength: {
                  value: 50,
                  message: 'Title must not exceed 50 characters'
                }
              })}
            />
            {errors.title && (
              <p className="mt-1 text-sm text-red-600">{errors.title.message}</p>
            )}
            <p className="mt-1 text-sm text-gray-500">
              Short title for the bubble (max 50 characters)
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Response <span className="text-red-500">*</span>
            </label>
            <div className="border border-gray-300 rounded-md">
              <TinyMCEEditor
                value={responseContent}
                onChange={setResponseContent}
                placeholder="Instant response when user clicks the bubble..."
              />
            </div>
            <p className="mt-1 text-sm text-gray-500">
              Pre-defined answer with HTML formatting support. Use <strong>bold</strong>, <em>italic</em>, lists, links, etc.
            </p>
            <div className="mt-2 text-xs text-gray-400">
              <strong>Examples:</strong> 
              • <code>&lt;strong&gt;Bold text&lt;/strong&gt;</code>
              • <code>&lt;a href="tel:+37012345"&gt;+370 123 45&lt;/a&gt;</code>
              • <code>&lt;ul&gt;&lt;li&gt;Item 1&lt;/li&gt;&lt;/ul&gt;</code>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Display Order
            </label>
            <input
              type="number"
              min="0"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              {...register('order')}
              defaultValue={0}
            />
            <p className="mt-1 text-sm text-gray-500">
              Lower numbers appear first (0 = highest priority)
            </p>
          </div>

          <div>
            <label className="flex items-center">
              <input
                type="checkbox"
                className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                {...register('isActive')}
                defaultChecked={true}
              />
              <span className="ml-2 text-sm text-gray-700">
                Active (show in widget)
              </span>
            </label>
          </div>

          <div className="flex justify-end space-x-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 hover:bg-gray-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50"
            >
              {isSubmitting ? 'Saving...' : (isEdit ? 'Update' : 'Create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default QuickQuestionModal;